import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  Phone, PhoneCall, CheckCircle2, XCircle, Clock,
  Voicemail, ThumbsDown, LogOut, Smartphone, Wifi, WifiOff,
  Zap, MessageSquare
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'reached', label: 'Erreicht', Icon: CheckCircle2, cls: 'border-emerald-500 text-emerald-700 bg-emerald-50' },
  { value: 'not_reached', label: 'Nicht erreicht', Icon: XCircle, cls: 'border-red-500 text-red-700 bg-red-50' },
  { value: 'mailbox', label: 'Mailbox', Icon: Voicemail, cls: 'border-blue-500 text-blue-700 bg-blue-50' },
  { value: 'callback', label: 'Rückruf', Icon: Clock, cls: 'border-amber-500 text-amber-700 bg-amber-50' },
  { value: 'no_interest', label: 'Kein Interesse', Icon: ThumbsDown, cls: 'border-slate-400 text-slate-600 bg-slate-50' },
];

// ─── Sound Helper ────────────────────────────────────────────────────────────
function playNotificationSound(audioCtx) {
  try {
    const ctx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === 'suspended') ctx.resume();
    // Two-tone alert
    [880, 1100, 880, 1100].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.value = 0.4;
      osc.start(ctx.currentTime + i * 0.15);
      osc.stop(ctx.currentTime + i * 0.15 + 0.12);
    });
  } catch { /* Audio not supported */ }
}

export default function MobilePage() {
  const { user, token, logout } = useAuth();
  const [activated, setActivated] = useState(true);
  const [mode, setMode] = useState('waiting'); // waiting | postcall
  const [currentCall, setCurrentCall] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastPoll, setLastPoll] = useState(null);
  const [dialingNumber, setDialingNumber] = useState(null);
  const [currentSms, setCurrentSms] = useState(null);
  const currentCallRef = useRef(null);
  const currentSmsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const wakeLockRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const { connected, lastMessage } = useWebSocket(token);

  // ─── Auto-init audio & wake lock on first user interaction ───────────────
  useEffect(() => {
    const initOnInteraction = async () => {
      if (audioCtxRef.current) return; // already initialized
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        gain.gain.value = 0;
        osc.start();
        osc.stop(ctx.currentTime + 0.01);
      } catch { /* ignore */ }
      try {
        if ('wakeLock' in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    document.addEventListener('touchstart', initOnInteraction, { once: true });
    document.addEventListener('click', initOnInteraction, { once: true });
    return () => {
      document.removeEventListener('touchstart', initOnInteraction);
      document.removeEventListener('click', initOnInteraction);
    };
  }, []);

  // Re-acquire Wake Lock when page becomes visible
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState === 'visible') {
        // Re-acquire wake lock
        if ('wakeLock' in navigator) {
          try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
          } catch { /* ignore */ }
        }
        // Check for pending calls (only recent ones, max 2 min old)
        if (!currentCallRef.current) {
          try {
            const res = await axios.get(`${API}/calls/pending`, { headers });
            if (res.data.length > 0) {
              const call = res.data[0];
              const diffMin = (Date.now() - new Date(call.created_at)) / 60000;
              if (diffMin < 1) {
                handleIncomingCall({
                  call_id: call.id, lead_id: call.lead_id,
                  lead_name: call.lead_name, phone_number: call.phone_number
                });
              }
            }
          } catch { /* ignore */ }
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token]);

  // Cleanup Wake Lock on unmount
  useEffect(() => {
    return () => {
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {});
    };
  }, []);

  // ─── Handle Incoming Call (Auto-Dial) ──────────────────────────────────────
  const handleIncomingCall = useCallback((call) => {
    const callId = call.call_id || call.id;
    if (currentCallRef.current === callId) return;
    currentCallRef.current = callId;
    setCurrentCall(call);
    setSelectedStatus(null);
    setNotes('');
    setCurrentSms(null);
    currentSmsRef.current = null;

    // Play sound
    playNotificationSound(audioCtxRef.current);

    // Vibrate aggressively
    if (navigator.vibrate) navigator.vibrate([300, 100, 300, 100, 300]);

    // AUTO-MODE: Go directly to postcall + open dialer
    setMode('postcall');
    setDialingNumber(call.phone_number);
    setTimeout(() => {
      window.location.href = `tel:${call.phone_number}`;
    }, 400);
  }, []);

  // ─── Helper: Open native app via link click (more reliable than location.href) ─
  const openNativeLink = useCallback((url) => {
    const link = document.createElement('a');
    link.href = url;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => link.remove(), 1000);
  }, []);

  // ─── Handle Incoming SMS (Auto-Open) ─────────────────────────────────────
  const handleIncomingSms = useCallback((sms) => {
    const smsId = sms.sms_id || sms.id;
    if (currentSmsRef.current === smsId) return;
    currentSmsRef.current = smsId;
    setCurrentSms(sms);

    // If in postcall mode, auto-save previous call as 'reached' and reset
    if (mode === 'postcall' && currentCall) {
      const prevCallId = currentCall.call_id || currentCall.id;
      axios.put(`${API}/calls/${prevCallId}/status`,
        { status: selectedStatus || 'reached', notes }, { headers }).catch(() => {});
      setCurrentCall(null);
      currentCallRef.current = null;
      setDialingNumber(null);
      setMode('waiting');
    }

    // Play sound
    playNotificationSound(audioCtxRef.current);
    if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

    // Open SMS app - try multiple methods for Android compatibility
    const body = encodeURIComponent(sms.message || '');
    const phone = sms.phone_number;
    setTimeout(() => {
      // Method 1: Link click (most reliable on Android)
      openNativeLink(`sms:${phone}?body=${body}`);
      // Method 2: Fallback with different URI format for some Android versions
      setTimeout(() => {
        if (document.hasFocus()) {
          openNativeLink(`sms:${phone}&body=${body}`);
        }
      }, 800);
    }, 400);
  }, [mode, currentCall, selectedStatus, notes, token, openNativeLink]);

  // Manual SMS open (fallback)
  const openSmsManually = () => {
    if (!currentSms) return;
    const smsId = currentSms.sms_id || currentSms.id;
    const body = encodeURIComponent(currentSms.message || '');
    openNativeLink(`sms:${currentSms.phone_number}?body=${body}`);
    setTimeout(async () => {
      try {
        await axios.put(`${API}/sms/${smsId}/status`,
          { status: 'sent' }, { headers });
      } catch { /* ignore */ }
      currentSmsRef.current = null;
      setCurrentSms(null);
    }, 2000);
  };

  const dismissSms = async () => {
    if (!currentSms) return;
    const smsId = currentSms.sms_id || currentSms.id;
    try {
      await axios.put(`${API}/sms/${smsId}/status`,
        { status: 'sent' }, { headers });
    } catch { /* ignore */ }
    currentSmsRef.current = null;
    setCurrentSms(null);
  };

  // Handle incoming WS messages (primary delivery)
  useEffect(() => {
    if (!lastMessage) return;
    if (lastMessage.type === 'incoming_call') {
      handleIncomingCall(lastMessage);
    }
    if (lastMessage.type === 'incoming_sms') {
      handleIncomingSms(lastMessage);
    }
  }, [lastMessage, handleIncomingCall, handleIncomingSms]);

  // POLLING FALLBACK: Check for pending calls + SMS every 3 seconds
  useEffect(() => {
    const pollPending = async () => {
      try {
        setLastPoll(new Date());
        // Check pending calls (only if not already handling one)
        if (!currentCallRef.current) {
          const callRes = await axios.get(`${API}/calls/pending`, { headers });
          if (callRes.data.length > 0) {
            const call = callRes.data[0];
            const diffMin = (Date.now() - new Date(call.created_at)) / 60000;
            if (diffMin < 1) {
              handleIncomingCall({
                call_id: call.id, lead_id: call.lead_id,
                lead_name: call.lead_name, phone_number: call.phone_number
              });
              return;
            }
          }
        }
        // Check pending SMS (only if not already handling one)
        if (!currentSmsRef.current) {
          const smsRes = await axios.get(`${API}/sms/pending`, { headers });
          if (smsRes.data.length > 0) {
            const sms = smsRes.data[0];
            const diffMin = (Date.now() - new Date(sms.created_at)) / 60000;
            if (diffMin < 1) {
              handleIncomingSms({
                sms_id: sms.id, lead_id: sms.lead_id,
                lead_name: sms.lead_name, phone_number: sms.phone_number,
                message: sms.message
              });
            }
          }
        }
      } catch { /* ignore */ }
    };
    pollPending();
    const interval = setInterval(pollPending, 3000);
    return () => clearInterval(interval);
  }, [token, handleIncomingCall, handleIncomingSms]);

  const handleCallInitiate = () => {
    setMode('postcall');
  };

  const handleSaveStatus = async () => {
    if (!selectedStatus) {
      toast.error('Bitte Status auswählen');
      return;
    }
    setSaving(true);
    try {
      const callId = currentCall.call_id || currentCall.id;
      await axios.put(`${API}/calls/${callId}/status`, {
        status: selectedStatus,
        notes
      }, { headers });
      toast.success('Status gespeichert');
      currentCallRef.current = null;
      setDialingNumber(null);
      setMode('waiting');
      setCurrentCall(null);
      setSelectedStatus(null);
      setNotes('');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    // Mark as skipped so polling doesn't re-show it
    try {
      const callId = currentCall.call_id || currentCall.id;
      await axios.put(`${API}/calls/${callId}/status`, {
        status: 'not_reached',
        notes: 'Übersprungen'
      }, { headers });
    } catch { /* ignore */ }
    currentCallRef.current = null;
    setDialingNumber(null);
    setMode('waiting');
    setCurrentCall(null);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="mobile-page">
      {/* Header */}
      <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-slate-400" />
          <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CallRelay
          </span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5" data-testid="mobile-connection-status">
            {connected ? (
              <Wifi className="w-4 h-4 text-emerald-500" />
            ) : (
              <WifiOff className="w-4 h-4 text-red-500" />
            )}
            <span className="text-xs text-slate-500">
              {connected ? 'Verbunden' : 'Getrennt'}
            </span>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} className="h-8 w-8 p-0" data-testid="mobile-logout-button">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 flex flex-col items-center justify-center p-6">

        {/* ── Waiting Mode (always active) ── */}
        {mode === 'waiting' && !currentSms && (
          <div className="text-center space-y-4" data-testid="waiting-screen">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto">
              <Zap className="w-10 h-10 text-emerald-500 animate-pulse" />
            </div>
            <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Auto-Modus aktiv
            </h2>
            <p className="text-slate-500 text-sm max-w-xs mx-auto">
              Wähler/SMS öffnet sich automatisch wenn ein Anruf oder eine SMS vom PC kommt.
            </p>
            <div className="flex items-center gap-2 justify-center pt-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs text-slate-400">
                {connected ? 'Live + Polling' : 'Polling (alle 3 Sek.)'}
              </span>
            </div>
            <p className="text-[10px] text-slate-300 pt-1">
              {user?.name} {lastPoll && `· ${lastPoll.toLocaleTimeString('de-DE', {hour:'2-digit', minute:'2-digit', second:'2-digit'})}`}
            </p>
          </div>
        )}

        {/* ── SMS Received Screen ── */}
        {currentSms && mode === 'waiting' && (
          <div className="text-center space-y-6 w-full max-w-sm" data-testid="sms-screen">
            <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto">
              <MessageSquare className="w-8 h-8 text-blue-500" />
            </div>
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-blue-600">
                SMS wird geöffnet
              </p>
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {currentSms.lead_name}
              </h2>
              <p className="text-lg font-mono text-slate-500">{currentSms.phone_number}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-sm text-left">
              <p className="text-sm text-slate-600">{currentSms.message}</p>
            </div>
            <button
              onClick={() => openNativeLink(`sms:${currentSms.phone_number}?body=${encodeURIComponent(currentSms.message || '')}`)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-4 text-lg font-bold text-center transition-colors active:scale-95"
              data-testid="retry-sms-button"
            >
              <span className="flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5" />
                SMS-App öffnen
              </span>
            </button>
            <button
              onClick={dismissSms}
              className="text-sm text-slate-400 hover:text-slate-600"
              data-testid="dismiss-sms-inline"
            >
              Fertig / Verwerfen
            </button>
          </div>
        )}

        {/* ── Incoming Call Mode (fallback when not activated) ── */}
        {mode === 'incoming' && currentCall && (
          <div className="text-center space-y-8 w-full max-w-sm" data-testid="incoming-call-screen">
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">
                Eingehender Anruf
              </p>
              <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {currentCall.lead_name}
              </h2>
              <p className="text-2xl font-mono font-medium text-slate-700 tracking-tight" data-testid="call-phone-number">
                {currentCall.phone_number}
              </p>
            </div>
            <a
              href={`tel:${currentCall.phone_number}`}
              onClick={handleCallInitiate}
              className="call-pulse block w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm px-8 py-5 text-xl font-bold text-center transition-colors active:scale-95"
              data-testid="call-now-button"
            >
              <span className="flex items-center justify-center gap-3">
                <PhoneCall className="w-6 h-6" />
                Jetzt anrufen
              </span>
            </a>
            <button
              onClick={handleSkip}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="skip-call-button"
            >
              Überspringen
            </button>
          </div>
        )}

        {/* ── Post Call Mode ── */}
        {mode === 'postcall' && currentCall && (
          <div className="w-full max-w-sm space-y-6" data-testid="postcall-screen">
            <div className="text-center space-y-1">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">
                {dialingNumber ? 'Wähler geöffnet' : 'Anruf beendet'}
              </p>
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {currentCall.lead_name}
              </h2>
              <p className="text-sm font-mono text-slate-500">{currentCall.phone_number}</p>
            </div>

            {/* Retry call link */}
            <a
              href={`tel:${currentCall.phone_number}`}
              className="block text-center text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              data-testid="retry-call-link"
            >
              <span className="flex items-center justify-center gap-1.5">
                <PhoneCall className="w-4 h-4" />
                Nochmal anrufen
              </span>
            </a>

            <Separator />

            <div className="space-y-3">
              <p className="text-sm font-medium text-slate-700">Ergebnis:</p>
              <div className="grid grid-cols-2 gap-3">
                {STATUS_OPTIONS.map(({ value, label, Icon, cls }) => {
                  const selected = selectedStatus === value;
                  return (
                    <button
                      key={value}
                      onClick={() => setSelectedStatus(value)}
                      className={`status-btn border-2 rounded-sm p-3 text-left transition-all ${
                        selected
                          ? `${cls} border-current font-semibold`
                          : 'border-slate-200 text-slate-600 hover:border-slate-300 bg-white'
                      }`}
                      data-testid={`status-${value}`}
                    >
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4 shrink-0" />
                        <span className="text-sm">{label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-slate-700">Notiz (optional):</p>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="Notizen zum Gespräch..."
                className="rounded-sm min-h-[80px]"
                data-testid="call-notes-input"
              />
            </div>

            <Button
              onClick={handleSaveStatus}
              disabled={saving || !selectedStatus}
              className="w-full rounded-sm bg-black text-white hover:bg-slate-800 py-5 text-base font-bold"
              data-testid="save-status-button"
            >
              {saving ? 'Speichern...' : 'Status speichern'}
            </Button>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="px-4 py-3 border-t border-slate-100 text-center shrink-0">
        <p className="text-xs text-slate-400">
          Angemeldet als {user?.name}
        </p>
      </footer>

      {/* ── SMS Overlay (shown when SMS received without auto-mode) ── */}
      {currentSms && !activated && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end" data-testid="sms-overlay">
          <div className="bg-white w-full rounded-t-2xl p-6 space-y-4 shadow-2xl animate-in slide-in-from-bottom">
            <div className="text-center space-y-1">
              <MessageSquare className="w-10 h-10 text-blue-500 mx-auto" />
              <p className="text-xs uppercase tracking-widest font-bold text-blue-600">Eingehende SMS</p>
              <h3 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {currentSms.lead_name}
              </h3>
              <p className="text-sm font-mono text-slate-500">{currentSms.phone_number}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-sm">
              <p className="text-sm text-slate-700">{currentSms.message}</p>
            </div>
            <a
              href={`sms:${currentSms.phone_number}?body=${encodeURIComponent(currentSms.message || '')}`}
              onClick={openSmsManually}
              className="block w-full bg-blue-600 hover:bg-blue-700 text-white rounded-sm px-6 py-4 text-lg font-bold text-center transition-colors active:scale-95"
              data-testid="open-sms-button"
            >
              <span className="flex items-center justify-center gap-2">
                <MessageSquare className="w-5 h-5" />
                SMS-App öffnen
              </span>
            </a>
            <button
              onClick={dismissSms}
              className="w-full text-sm text-slate-400 hover:text-slate-600 py-2"
              data-testid="dismiss-sms-button"
            >
              Verwerfen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
